import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { Button } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface StepperFooterProps {
    currentStepIndex: number;
    isNextStepLoading: boolean;
    onBack: () => void;
    onNext: () => void;
    onSubmit: () => void;
    stepCount: number;
    disableSubmit?: boolean;
    submitTooltipText?: string;
}

interface StepperFooterStyleProps {
    isFirstStep: boolean;
}

const useStyles = createUseStyles({
    container: ({ isFirstStep }: StepperFooterStyleProps) => ({
        borderTop: `solid 1px ${colors.grayThree}`,
        height: 136,
        justifyContent: !isFirstStep ? 'space-between' : 'flex-end',
    }),
});

export function StepperFooter({
    currentStepIndex,
    isNextStepLoading,
    onBack,
    onNext,
    onSubmit,
    stepCount,
    disableSubmit,
    submitTooltipText,
}: StepperFooterProps) {
    const { t } = useTranslation();
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === stepCount - 1;
    const classes = useStyles({ isFirstStep });
    const nextStepButtonText = !isLastStep ? t('buttons:continue') : t('buttons:submit');
    const isSubmitDisabled = isLastStep && disableSubmit;

    const submitButton = (
        <Button onPress={onNext} isLoading={isNextStepLoading} disabled={isSubmitDisabled}>
            {nextStepButtonText}
        </Button>
    );

    return (
        <div className={`${classes.container} d-flex align-items-center flex-shrink-0 px-8`}>
            {!isFirstStep && (
                <Button variant="primary-blue-outline" onPress={onBack}>
                    {t('buttons:back')}
                </Button>
            )}
            {isSubmitDisabled && submitTooltipText ? (
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="footer-submit-tooltip">{submitTooltipText}</Tooltip>}
                >
                    <span>{submitButton}</span>
                </OverlayTrigger>
            ) : (
                submitButton
            )}
        </div>
    );
}
